import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Close, TrendingUp, TrendingDown, Group, Analytics } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { GET_DISCOUNT_COHORT_ANALYSIS } from '../discount.graphql';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDateOnly } from '../../Formatters';

interface RetentionDetailsProps {
  open: boolean;
  onClose: () => void;
  discountName: string;
}

interface YearlyActivity {
  year: number;
  activeCustomers: number;
}

interface CohortYearData {
  cohortYear: number;
  initialCustomers: number;
  yearlyActivity: YearlyActivity[];
}

interface YearlyTotal {
  year: number;
  totalActiveCustomers: number;
}

interface YearOverYearRetention {
  fromYear: number;
  toYear: number;
  retentionRate: number;
}

interface CohortAnalysisResult {
  discountName: string;
  discountId: string;
  cohortData: CohortYearData[];
  yearlyTotals: YearlyTotal[];
  retentionRates: YearOverYearRetention[];
  analysisStartYear: number;
  analysisEndYear: number;
  totalCustomersAnalyzed: number;
  analysisDate: string;
  errorMessage?: string;
}

interface QueryResult {
  getDiscountCohortAnalysis: CohortAnalysisResult;
}

export function RetentionDetails({ open, onClose, discountName }: RetentionDetailsProps) {
  const { loading, data, error } = useQuery<QueryResult>(GET_DISCOUNT_COHORT_ANALYSIS, {
    variables: { 
      discountName,
      yearsBack: 6,
      startYear: null,
      endYear: null
    },
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  const analysisData = data?.getDiscountCohortAnalysis;
  console.log({analysisData});

  // Helper function to convert yearlyActivity array to lookup object
  const getYearlyActivityLookup = (yearlyActivity: YearlyActivity[]) => {
    return yearlyActivity.reduce((acc, item) => {
      acc[item.year] = item.activeCustomers;
      return acc;
    }, {} as Record<number, number>);
  };

  // Helper function to convert yearlyTotals array to lookup object
  const getYearlyTotalsLookup = (yearlyTotals: YearlyTotal[]) => {
    return yearlyTotals.reduce((acc, item) => {
      acc[item.year] = item.totalActiveCustomers;
      return acc;
    }, {} as Record<number, number>);
  };

  const renderRetentionTable = () => {
    if (!analysisData || !analysisData.cohortData.length) return null;

    const years = Array.from(
      { length: analysisData.analysisEndYear - analysisData.analysisStartYear + 1 },
      (_, i) => analysisData.analysisStartYear + i
    );

    const yearlyTotalsLookup = getYearlyTotalsLookup(analysisData.yearlyTotals);

    return (
      <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                Kohort År
              </TableCell>
              {years.map(year => (
                <TableCell key={year} align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>
                  {year}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {analysisData.cohortData.map(cohort => {
              const activityLookup = getYearlyActivityLookup(cohort.yearlyActivity);
              
              return (
                <TableRow key={cohort.cohortYear} hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    {cohort.cohortYear} Kohort
                  </TableCell>
                  {years.map(year => {
                    const count = activityLookup[year];
                    const retentionPercent = cohort.initialCustomers > 0 
                      ? ((count || 0) / cohort.initialCustomers * 100).toFixed(1)
                      : '0.0';
                    
                    return (
                      <TableCell key={year} align="center">
                        {count !== undefined ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {count}
                            </Typography>
                            {year !== cohort.cohortYear && (
                              <Typography variant="caption" sx={{
                                color: "text.secondary"
                              }}>
                                ({retentionPercent}%)
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{
                            color: "text.disabled"
                          }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Totalt Aktive
              </TableCell>
              {years.map(year => (
                <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                  {yearlyTotalsLookup[year] || 0}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderRetentionRates = () => {
    if (!analysisData?.retentionRates.length) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp />
          Retention Rates
        </Typography>
        <Grid container spacing={2}>
          {analysisData.retentionRates.map(retention => {
            const rate = retention.retentionRate;
            const isGoodRetention = rate >= 70;
            const isWarningRetention = rate >= 50 && rate < 70;
            
            return (
              <Grid
                key={`${retention.fromYear}-${retention.toYear}`}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" sx={{ 
                      color: isGoodRetention ? 'success.main' : isWarningRetention ? 'warning.main' : 'error.main',
                      fontWeight: 'bold'
                    }}>
                      {rate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: "text.secondary"
                    }}>
                      {retention.fromYear} → {retention.toYear}
                    </Typography>
                    {isGoodRetention ? (
                      <TrendingUp color="success" sx={{ mt: 1 }} />
                    ) : (
                      <TrendingDown color="error" sx={{ mt: 1 }} />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderSummaryStats = () => {
    if (!analysisData) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Group color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="primary">
                {analysisData.totalCustomersAnalyzed}
              </Typography>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Kunder Analysert
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Analytics color="secondary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="secondary">
                {analysisData.analysisEndYear - analysisData.analysisStartYear + 1}
              </Typography>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                År Analysert
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{
                color: "info.main"
              }}>
                {analysisData.analysisStartYear}
              </Typography>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Start År
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{
                color: "info.main"
              }}>
                {analysisData.analysisEndYear}
              </Typography>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Slutt År
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
     
     slotProps={{paper: {
        sx: { minHeight: '70vh' }
      }}}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Analytics color="primary" />
            <Typography variant="h6">
              Retention Analyse - {discountName}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'grey.500' }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LoadingSpinner />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Feil ved henting av retention data: {error.message}
          </Alert>
        )}
        
        {analysisData?.errorMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {analysisData.errorMessage}
          </Alert>
        )}
        
        {analysisData && !analysisData.errorMessage && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Analyse utført den {formatDateOnly(analysisData.analysisDate)} for rabatt ID: {analysisData.discountId}
            </Alert>
            
            {renderSummaryStats()}
            {renderRetentionRates()}
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group />
              Kohort Tabell
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              Viser antall aktive kunder per år for hver kohort. Prosentandeler viser retention rate sammenlignet med første år.
            </Typography>
            
            {renderRetentionTable()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Lukk
        </Button>
      </DialogActions>
    </Dialog>
  );
}